// owner/screens/unit/UnitReviews.js — a unit's booking-site (OTA) reviews, newest first, 20 at a time. Direct-booking reviews are
// not shown (Adil, 4 Nov 2026). Scores are /10, the stored canonical scale; the date is when the review arrived
// (`sourceReceivedAt`, what the API sorts by), shown as a day.

import React from 'react';
import useLanguage from '../../../hooks/useLanguage';
import { fill } from '../../../utils/localeText';
import PlatformMark from '../../ui/PlatformMark';
import { InlineError } from '../../ui/States';
import useOwnerFormat from '../../ui/useOwnerFormat';
import { useUnitReviews } from '../../api/ownerReads';

const UnitReviews = ({ unitId }) => {
  const { t } = useLanguage();
  const f = useOwnerFormat();
  const reviews = useUnitReviews(unitId);

  if (reviews.isLoading) return <div className="o-sk" style={{ height: 220, borderRadius: 20 }} aria-busy="true" />;
  if (reviews.isError && reviews.reviews.length === 0) {
    return <div className="o-sheet"><InlineError text={t('ownerApp.unit.reviewsError')} onRetry={reviews.refetch} /></div>;
  }

  const rows = reviews.reviews;
  if (rows.length === 0) return <div className="o-sheet"><p className="o-empty-row">{t('ownerApp.unit.reviewsEmpty')}</p></div>;

  return (
    <div className="o-sheet">
      {rows.map((r) => (
        <article key={r._id} className="o-review">
          <div className="o-row" style={{ padding: 0, minHeight: 0 }}>
            <div className="o-body">
              <p className="o-t">{r.guestName || t('ownerApp.unit.guestFallback')}</p>
              <p className="o-m">
                <PlatformMark platform={r.ota || r.source} withName />
                {r.sourceReceivedAt && <span>{f.date(r.sourceReceivedAt)}</span>}
              </p>
            </div>
            {Number.isFinite(r.overallScore) && (
              <span className="o-score">{fill(t('ownerApp.unit.score'), { score: f.count(r.overallScore) })}</span>
            )}
          </div>
          {r.content && <p dir="auto">{r.content}</p>}
        </article>
      ))}
      <div className="o-list-foot">
        <span>{fill(t('lists.showingOf'), { shown: f.count(rows.length), total: f.count(reviews.total) })}</span>
        {reviews.hasNextPage && (
          <button type="button" className="o-btn o-btn-outline" onClick={() => reviews.fetchNextPage()} disabled={reviews.isFetchingNextPage}>
            {reviews.isFetchingNextPage ? t('lists.loadingMore') : t('lists.loadMore')}
          </button>
        )}
      </div>
      {reviews.isFetchNextPageError && <InlineError text={t('lists.loadMoreFailed')} onRetry={() => reviews.fetchNextPage()} />}
    </div>
  );
};

export default UnitReviews;
